import React, { useState, useEffect, useMemo } from 'react';
import Modal from '../Modal/Modal';
import styles from './NewsModal.module.css';
import * as FaIcons from 'react-icons/fa';
import packageJson from '../../../../package.json';
import changelogData from '../../../data/CHANGELOG_USER.json';
import enTranslations from '../../../translate/en.json';
import esTranslations from '../../../translate/es.json';
import ptTranslations from '../../../translate/pt.json';

const NewsModal = ({ isOpen, onClose, lastSeenVersion }) => {
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [filteredChangelog, setFilteredChangelog] = useState([]);

  // Obtener idioma actual
  const getCurrentLanguage = () => {
    const langStorage = localStorage.getItem("language");
    return langStorage || window.navigator.language?.split("-")[0] || 'en';
  };

  // Obtener traducciones y configuraciones según el idioma
  const { translations, dateLocale, currentLang } = useMemo(() => {
    const lang = getCurrentLanguage();
    const languageMap = {
      es: {
        translations: esTranslations,
        dateLocale: 'es-ES'
      },
      pt: {
        translations: ptTranslations,
        dateLocale: 'pt-BR'
      },
      en: {
        translations: enTranslations,
        dateLocale: 'en-US'
      }
    };
    return { ...languageMap[lang] || languageMap['en'], currentLang: lang };
  }, [isOpen]); // Se recalcula cuando el modal se abre

  // Función para obtener los cambios según el idioma actual
  const getChangesForLanguage = (version) => {
    // Si changes es un objeto con idiomas (estructura nueva)
    if (version.changes && typeof version.changes === 'object' && !Array.isArray(version.changes)) {
      // Prioridad: idioma actual > inglés > primer idioma disponible
      return version.changes[currentLang] || 
             version.changes['en'] || 
             version.changes[Object.keys(version.changes)[0]] || [];
    }
    // Compatibilidad: si changes es un array (formato antiguo), retornarlo tal cual
    return version.changes || [];
  };

  useEffect(() => {
    if (isOpen && changelogData && Array.isArray(changelogData)) {
      // Filtrar solo las versiones nuevas si hay lastSeenVersion
      if (lastSeenVersion) {
        const currentVersionIndex = changelogData.findIndex(
          v => v.version === packageJson.version
        );
        const lastSeenIndex = changelogData.findIndex(
          v => v.version === lastSeenVersion
        );
        
        if (lastSeenIndex >= 0 && currentVersionIndex >= 0 && currentVersionIndex > lastSeenIndex) {
          setFilteredChangelog(
            changelogData.slice(0, currentVersionIndex - lastSeenIndex + 1)
          );
        } else {
          // Mostrar solo las últimas 5 versiones
          setFilteredChangelog(changelogData.slice(0, 5));
        }
      } else {
        // Mostrar solo las últimas 5 versiones
        setFilteredChangelog(changelogData.slice(0, 5));
      }
      
      // Seleccionar la primera versión por defecto
      if (changelogData.length > 0) {
        setSelectedVersion(changelogData[0]);
      }
    }
  }, [isOpen, lastSeenVersion]);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      // Usar el locale correspondiente al idioma actual
      return date.toLocaleDateString(dateLocale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const handleVersionClick = (version) => {
    setSelectedVersion(version);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={translations['newsModal.title']}
      size="lg"
      className={styles.newsModal}
    >
      <div className={styles.container}>
        <div className={styles.sidebar}>
          <h3 className={styles.sidebarTitle}>
            {translations['newsModal.versions']}
          </h3>
          <div className={styles.versionList}>
            {filteredChangelog.map((version, index) => (
              <button
                key={index}
                className={`${styles.versionItem} ${
                  selectedVersion?.version === version.version ? styles.active : ''
                }`}
                onClick={() => handleVersionClick(version)}
              >
                <div className={styles.versionNumber}>
                  {version.version}
                </div>
                <div className={styles.versionDate}>
                  {formatDate(version.date)}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.content}>
          {selectedVersion ? (
            <>
              <div className={styles.versionHeader}>
                <h2 className={styles.versionTitle}>
                  {translations['newsModal.version']} {selectedVersion.version}
                </h2>
                <span className={styles.versionDateBadge}>
                  {formatDate(selectedVersion.date)}
                </span>
              </div>

              <div className={styles.changesList}>
                {(() => {
                  const changes = getChangesForLanguage(selectedVersion);
                  return changes.length > 0 ? (
                    changes.map((change, index) => (
                      <div key={index} className={styles.changeItem}>
                        <FaIcons.FaCheckCircle className={styles.changeIcon} />
                        <span className={styles.changeText}>{change}</span>
                      </div>
                    ))
                  ) : (
                    <p>{translations['newsModal.noChanges']}</p>
                  );
                })()}
              </div>
            </>
          ) : (
            <div className={styles.emptyState}>
              <p>{translations['newsModal.noChanges']}</p>
            </div>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        <button
          className={styles.closeButton}
          onClick={onClose}
        >
          {translations['newsModal.close']}
        </button>
      </div>
    </Modal>
  );
};

export default NewsModal;

